// Soccer Countries, Leagues, and Teams Data Structure
// This enables hierarchical filtering: Country -> League -> Team

export interface League {
  id: string;
  name: string;
  country: string;
  teams: Team[];
}

export interface Team {
  id: string;
  name: string;
  city?: string;
  founded?: number;
}

export interface Country {
  id: string;
  name: string;
  code: string; // ISO country code
  leagues: League[];
}

// Major soccer countries with their leagues and teams
export const SOCCER_COUNTRIES: Country[] = [
  {
    id: 'england',
    name: 'England',
    code: 'ENG',
    leagues: [
      {
        id: 'premier-league',
        name: 'Premier League',
        country: 'England',
        teams: [
          { id: 'arsenal', name: 'Arsenal', city: 'London' },
          { id: 'chelsea', name: 'Chelsea', city: 'London' },
          { id: 'liverpool', name: 'Liverpool', city: 'Liverpool' },
          { id: 'man-city', name: 'Manchester City', city: 'Manchester' },
          { id: 'man-united', name: 'Manchester United', city: 'Manchester' },
          { id: 'tottenham', name: 'Tottenham Hotspur', city: 'London' },
          { id: 'newcastle', name: 'Newcastle United', city: 'Newcastle' },
          { id: 'brighton', name: 'Brighton & Hove Albion', city: 'Brighton' },
          { id: 'aston-villa', name: 'Aston Villa', city: 'Birmingham' },
          { id: 'west-ham', name: 'West Ham United', city: 'London' },
          { id: 'crystal-palace', name: 'Crystal Palace', city: 'London' },
          { id: 'fulham', name: 'Fulham', city: 'London' },
          { id: 'wolves', name: 'Wolverhampton Wanderers', city: 'Wolverhampton' },
          { id: 'everton', name: 'Everton', city: 'Liverpool' },
          { id: 'brentford', name: 'Brentford', city: 'London' },
          { id: 'nottingham-forest', name: 'Nottingham Forest', city: 'Nottingham' },
          { id: 'bournemouth', name: 'AFC Bournemouth', city: 'Bournemouth' },
          { id: 'luton', name: 'Luton Town', city: 'Luton' },
          { id: 'burnley', name: 'Burnley', city: 'Burnley' },
          { id: 'sheffield-united', name: 'Sheffield United', city: 'Sheffield' }
        ]
      },
      {
        id: 'championship',
        name: 'EFL Championship',
        country: 'England',
        teams: [
          { id: 'leicester', name: 'Leicester City', city: 'Leicester' },
          { id: 'leeds', name: 'Leeds United', city: 'Leeds' },
          { id: 'southampton', name: 'Southampton', city: 'Southampton' },
          { id: 'middlesbrough', name: 'Middlesbrough', city: 'Middlesbrough' },
          { id: 'west-brom', name: 'West Bromwich Albion', city: 'West Bromwich' }
        ]
      }
    ]
  },
  {
    id: 'spain',
    name: 'Spain',
    code: 'ESP',
    leagues: [
      {
        id: 'la-liga',
        name: 'La Liga',
        country: 'Spain',
        teams: [
          { id: 'real-madrid', name: 'Real Madrid', city: 'Madrid' },
          { id: 'barcelona', name: 'FC Barcelona', city: 'Barcelona' },
          { id: 'atletico-madrid', name: 'Atlético Madrid', city: 'Madrid' },
          { id: 'sevilla', name: 'Sevilla FC', city: 'Sevilla' },
          { id: 'real-sociedad', name: 'Real Sociedad', city: 'San Sebastián' },
          { id: 'athletic-bilbao', name: 'Athletic Bilbao', city: 'Bilbao' },
          { id: 'valencia', name: 'Valencia CF', city: 'Valencia' },
          { id: 'villarreal', name: 'Villarreal CF', city: 'Villarreal' },
          { id: 'real-betis', name: 'Real Betis', city: 'Sevilla' },
          { id: 'celta-vigo', name: 'Celta de Vigo', city: 'Vigo' },
          { id: 'getafe', name: 'Getafe CF', city: 'Getafe' },
          { id: 'osasuna', name: 'CA Osasuna', city: 'Pamplona' },
          { id: 'las-palmas', name: 'UD Las Palmas', city: 'Las Palmas' },
          { id: 'girona', name: 'Girona FC', city: 'Girona' },
          { id: 'alaves', name: 'Deportivo Alavés', city: 'Vitoria-Gasteiz' }
        ]
      }
    ]
  },
  {
    id: 'italy',
    name: 'Italy',
    code: 'ITA',
    leagues: [
      {
        id: 'serie-a',
        name: 'Serie A',
        country: 'Italy',
        teams: [
          { id: 'juventus', name: 'Juventus', city: 'Turin' },
          { id: 'ac-milan', name: 'AC Milan', city: 'Milan' },
          { id: 'inter-milan', name: 'Inter Milan', city: 'Milan' },
          { id: 'napoli', name: 'SSC Napoli', city: 'Naples' },
          { id: 'roma', name: 'AS Roma', city: 'Rome' },
          { id: 'lazio', name: 'SS Lazio', city: 'Rome' },
          { id: 'atalanta', name: 'Atalanta', city: 'Bergamo' },
          { id: 'fiorentina', name: 'ACF Fiorentina', city: 'Florence' },
          { id: 'torino', name: 'Torino FC', city: 'Turin' },
          { id: 'bologna', name: 'Bologna FC', city: 'Bologna' },
          { id: 'sassuolo', name: 'US Sassuolo', city: 'Sassuolo' },
          { id: 'udinese', name: 'Udinese', city: 'Udine' },
          { id: 'monza', name: 'AC Monza', city: 'Monza' },
          { id: 'lecce', name: 'US Lecce', city: 'Lecce' }
        ]
      }
    ]
  },
  {
    id: 'germany',
    name: 'Germany',
    code: 'GER',
    leagues: [
      {
        id: 'bundesliga',
        name: 'Bundesliga',
        country: 'Germany',
        teams: [
          { id: 'bayern-munich', name: 'Bayern Munich', city: 'Munich' },
          { id: 'borussia-dortmund', name: 'Borussia Dortmund', city: 'Dortmund' },
          { id: 'rb-leipzig', name: 'RB Leipzig', city: 'Leipzig' },
          { id: 'bayer-leverkusen', name: 'Bayer Leverkusen', city: 'Leverkusen' },
          { id: 'eintracht-frankfurt', name: 'Eintracht Frankfurt', city: 'Frankfurt' },
          { id: 'borussia-monchengladbach', name: 'Borussia Mönchengladbach', city: 'Mönchengladbach' },
          { id: 'wolfsburg', name: 'VfL Wolfsburg', city: 'Wolfsburg' },
          { id: 'stuttgart', name: 'VfB Stuttgart', city: 'Stuttgart' },
          { id: 'werder-bremen', name: 'Werder Bremen', city: 'Bremen' },
          { id: 'hoffenheim', name: 'TSG Hoffenheim', city: 'Hoffenheim' },
          { id: 'freiburg', name: 'SC Freiburg', city: 'Freiburg' },
          { id: 'mainz', name: 'FSV Mainz 05', city: 'Mainz' }
        ]
      }
    ]
  },
  {
    id: 'france',
    name: 'France',
    code: 'FRA',
    leagues: [
      {
        id: 'ligue-1',
        name: 'Ligue 1',
        country: 'France',
        teams: [
          { id: 'psg', name: 'Paris Saint-Germain', city: 'Paris' },
          { id: 'marseille', name: 'Olympique de Marseille', city: 'Marseille' },
          { id: 'lyon', name: 'Olympique Lyonnais', city: 'Lyon' },
          { id: 'monaco', name: 'AS Monaco', city: 'Monaco' },
          { id: 'lille', name: 'LOSC Lille', city: 'Lille' },
          { id: 'nice', name: 'OGC Nice', city: 'Nice' },
          { id: 'rennes', name: 'Stade Rennais', city: 'Rennes' },
          { id: 'strasbourg', name: 'RC Strasbourg', city: 'Strasbourg' },
          { id: 'nantes', name: 'FC Nantes', city: 'Nantes' },
          { id: 'montpellier', name: 'Montpellier HSC', city: 'Montpellier' },
          { id: 'reims', name: 'Stade de Reims', city: 'Reims' },
          { id: 'lens', name: 'RC Lens', city: 'Lens' }
        ]
      }
    ]
  },
  {
    id: 'netherlands',
    name: 'Netherlands',
    code: 'NED',
    leagues: [
      {
        id: 'eredivisie',
        name: 'Eredivisie',
        country: 'Netherlands',
        teams: [
          { id: 'ajax', name: 'AFC Ajax', city: 'Amsterdam' },
          { id: 'psv', name: 'PSV Eindhoven', city: 'Eindhoven' },
          { id: 'feyenoord', name: 'Feyenoord', city: 'Rotterdam' },
          { id: 'az-alkmaar', name: 'AZ Alkmaar', city: 'Alkmaar' },
          { id: 'fc-twente', name: 'FC Twente', city: 'Enschede' },
          { id: 'fc-utrecht', name: 'FC Utrecht', city: 'Utrecht' },
          { id: 'vitesse', name: 'Vitesse', city: 'Arnhem' }
        ]
      }
    ]
  },
  {
    id: 'portugal',
    name: 'Portugal',
    code: 'POR',
    leagues: [
      {
        id: 'primeira-liga',
        name: 'Primeira Liga',
        country: 'Portugal',
        teams: [
          { id: 'porto', name: 'FC Porto', city: 'Porto' },
          { id: 'benfica', name: 'SL Benfica', city: 'Lisbon' },
          { id: 'sporting', name: 'Sporting CP', city: 'Lisbon' },
          { id: 'braga', name: 'SC Braga', city: 'Braga' },
          { id: 'vitoria-guimaraes', name: 'Vitória SC', city: 'Guimarães' }
        ]
      }
    ]
  },
  {
    id: 'brazil',
    name: 'Brazil',
    code: 'BRA',
    leagues: [
      {
        id: 'serie-a-brasil',
        name: 'Campeonato Brasileiro Série A',
        country: 'Brazil',
        teams: [
          { id: 'santos', name: 'Santos FC', city: 'Santos' },
          { id: 'sao-paulo', name: 'São Paulo FC', city: 'São Paulo' },
          { id: 'corinthians', name: 'Sport Club Corinthians Paulista', city: 'São Paulo' },
          { id: 'palmeiras', name: 'SE Palmeiras', city: 'São Paulo' },
          { id: 'flamengo', name: 'CR Flamengo', city: 'Rio de Janeiro' },
          { id: 'fluminense', name: 'Fluminense FC', city: 'Rio de Janeiro' },
          { id: 'botafogo', name: 'Botafogo de Futebol e Regatas', city: 'Rio de Janeiro' },
          { id: 'gremio', name: 'Grêmio FBPA', city: 'Porto Alegre' },
          { id: 'internacional', name: 'Sport Club Internacional', city: 'Porto Alegre' }
        ]
      }
    ]
  },
  {
    id: 'argentina',
    name: 'Argentina',
    code: 'ARG',
    leagues: [
      {
        id: 'primera-division',
        name: 'Liga Profesional de Fútbol',
        country: 'Argentina',
        teams: [
          { id: 'boca-juniors', name: 'Club Atlético Boca Juniors', city: 'Buenos Aires' },
          { id: 'river-plate', name: 'Club Atlético River Plate', city: 'Buenos Aires' },
          { id: 'racing', name: 'Racing Club', city: 'Avellaneda' },
          { id: 'independiente', name: 'Club Atlético Independiente', city: 'Avellaneda' },
          { id: 'san-lorenzo', name: 'Club Atlético San Lorenzo de Almagro', city: 'Buenos Aires' }
        ]
      }
    ]
  },
  {
    id: 'usa',
    name: 'United States',
    code: 'USA',
    leagues: [
      {
        id: 'mls',
        name: 'Major League Soccer (MLS)',
        country: 'United States',
        teams: [
          { id: 'inter-miami', name: 'Inter Miami CF', city: 'Miami' },
          { id: 'la-galaxy', name: 'LA Galaxy', city: 'Los Angeles' },
          { id: 'lafc', name: 'Los Angeles FC', city: 'Los Angeles' },
          { id: 'atlanta-united', name: 'Atlanta United FC', city: 'Atlanta' },
          { id: 'ny-city', name: 'New York City FC', city: 'New York' },
          { id: 'ny-red-bulls', name: 'New York Red Bulls', city: 'New York' },
          { id: 'seattle-sounders', name: 'Seattle Sounders FC', city: 'Seattle' },
          { id: 'toronto-fc', name: 'Toronto FC', city: 'Toronto' }
        ]
      }
    ]
  }
];

// Helper functions for data access
export const getCountries = (): Country[] => SOCCER_COUNTRIES;

export const getLeaguesByCountry = (countryId: string): League[] => {
  const country = SOCCER_COUNTRIES.find(c => c.id === countryId);
  return country?.leagues || [];
};

export const getTeamsByLeague = (countryId: string, leagueId: string): Team[] => {
  const country = SOCCER_COUNTRIES.find(c => c.id === countryId);
  const league = country?.leagues.find(l => l.id === leagueId);
  return league?.teams || [];
};

export const getAllTeams = (): Team[] => {
  return SOCCER_COUNTRIES.flatMap(country => 
    country.leagues.flatMap(league => league.teams)
  );
};

export const getAllLeagues = (): League[] => {
  return SOCCER_COUNTRIES.flatMap(country => country.leagues);
};

export const searchTeams = (query: string): Team[] => {
  const allTeams = getAllTeams();
  return allTeams.filter(team => 
    team.name.toLowerCase().includes(query.toLowerCase())
  );
};

export const searchLeagues = (query: string): League[] => {
  const allLeagues = getAllLeagues();
  return allLeagues.filter(league => 
    league.name.toLowerCase().includes(query.toLowerCase())
  );
}; 